import { Alert, Flex, Typography } from "antd";
import { QrcodeOutlined } from "@ant-design/icons";
import { formatDateTimeCustomize } from "../../../utils/dateTime";
import { border, brand, neutral, radius, spacing, typography } from "../../../theme/tokens";
import StatusTag from "../../../components/cms/StatusTag";

const { Text } = Typography;

// Loại mã -> không tô màu; chỉ TRẠNG THÁI mã ở dưới mới dùng màu.
const CODE_TYPE_LABEL = {
  SANPHAM: { label: "Mã sản phẩm" },
  GAME: { label: "Mã trò chơi" },
  OTHER: { label: "Khác" },
};

const CODE_STATUS_LABEL = {
  UNUSED: { label: "Chưa dùng", color: "default" },
  USED: { label: "Đã dùng", color: "green" },
  EXPIRED: { label: "Hết hạn", color: "red" },
};

/**
 * Thông tin mã đã quét — chỉ hiện với phiếu nguồn "Quét QR" (`MINIAPP_SCAN`).
 *
 * `code` là bản chụp lưu ở `mcoupon_point_histories.scanned_code`; `record` là bản
 * ghi `gamification_codes` nối theo chuỗi mã đó. Hai thứ tách nhau vì liên kết là
 * MỀM (nối theo `code`, chưa có khóa ngoại): mã bị xóa thì `record` null trong khi
 * `code` vẫn còn — phải nói rõ cho người dùng thay vì im lặng hiện thiếu.
 */
export default function ScannedCodePanel({ code, record, items = [] }) {
  if (!code) return null;

  const type = CODE_TYPE_LABEL[record?.type] || CODE_TYPE_LABEL.OTHER;
  const status = CODE_STATUS_LABEL[record?.status];

  /*
   * Đối soát: sản phẩm ghi trên phiếu phải trùng sản phẩm gắn với mã.
   * Luồng quét hiện tại tính điểm từ chính `service_product_id` của mã nên hai bên
   * luôn khớp — lệch nghĩa là dữ liệu bị sửa tay hoặc luồng ghi có lỗi, đáng để
   * người vận hành biết ngay trên màn đối soát.
   */
  const codeProductId = record?.service_product_id;
  const mismatch =
    codeProductId != null &&
    items.length > 0 &&
    !items.some((it) => Number(it.service_product_id) === Number(codeProductId));

  const fields = record
    ? [
        { label: "Lô phát hành", value: record.batch_no || "—" },
        {
          label: "Loại mã",
          value: <StatusTag>{type.label}</StatusTag>,
        },
        {
          label: "Sản phẩm của mã",
          value: record.product
            ? `${record.product.name}${record.product.code ? ` (${record.product.code})` : ""}`
            : "—",
        },
        { label: "Đơn vị phát hành", value: record.branch?.name || "—" },
        {
          label: "Trạng thái mã",
          value: status ? <StatusTag tone={status.color}>{status.label}</StatusTag> : "—",
        },
        {
          label: "Hạn sử dụng",
          value: record.expires_at ? formatDateTimeCustomize(record.expires_at) : "Không giới hạn",
        },
        {
          // Thời điểm quét THẬT trên bản ghi mã; `point_history.created_at` chỉ là
          // lúc ghi phiếu — hai mốc này lệch nhau nếu về sau xử lý bất đồng bộ.
          label: "Thời điểm quét",
          value: record.used_at ? formatDateTimeCustomize(record.used_at) : "—",
        },
        {
          label: "Mã tạo lúc",
          value: record.created_at ? formatDateTimeCustomize(record.created_at) : "—",
        },
        {
          label: "Người tạo mã",
          value: record.creator?.info?.name || "Hệ thống",
        },
      ]
    : [];

  return (
    <div style={{ marginBottom: spacing[5] }}>
      <Flex align="center" gap={spacing[2]} style={{ marginBottom: spacing[3] }}>
        <QrcodeOutlined style={{ color: brand.accent }} />
        <Text strong style={{ color: neutral[800] }}>
          Mã đã quét
        </Text>
        <StatusTag style={{ fontFamily: "monospace" }}>{code}</StatusTag>
      </Flex>

      {!record ? (
        <Alert
          type="warning"
          showIcon
          message="Không tìm thấy bản ghi của mã này"
          description="Mã đã bị xóa khỏi kho mã sau khi phiếu được tạo. Phiếu vẫn giữ nguyên chuỗi mã đã quét ở trên, nhưng không tra được lô, sản phẩm và hạn dùng."
        />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            gap: `${spacing[3]}px ${spacing[5]}px`,
            padding: spacing[4],
            background: neutral[50],
            border: border.hairline,
            borderRadius: radius.md,
          }}
        >
          {fields.map((f) => (
            <div key={f.label}>
              <Text
                style={{
                  display: "block",
                  color: neutral[400],
                  fontSize: typography.size.xs,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                  marginBottom: 2,
                }}
              >
                {f.label}
              </Text>
              <div style={{ color: neutral[800], fontSize: typography.size.md }}>
                {f.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {mismatch && (
        <Alert
          style={{ marginTop: spacing[3] }}
          type="error"
          showIcon
          message="Sản phẩm trên phiếu không khớp sản phẩm của mã"
          description="Điểm của phiếu này được tính cho sản phẩm khác với sản phẩm gắn vào mã đã quét. Cần kiểm tra lại dữ liệu trước khi đối soát."
        />
      )}
    </div>
  );
}
